import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import jsCookie from 'js-cookie'

export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_BASE_URL}`,
        prepareHeaders: (headers, { getState }) => {
            const token = jsCookie.get('token');
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['Orgs', 'Stocks', 'Jobs', 'Suppliers', 'Modules', 'Polyhouses'],
    endpoints: (builder) => ({
        //USERS / ORGS CRUD
        addUser: builder.mutation({
            query: ({ values, endpoint }) => ({
                url: `${endpoint}`,
                method: 'POST',
                body: { ...values },
            }),
            invalidatesTags: ['Orgs'],
        }),

        fetchUsers: builder.query({
            query: ({ endpoint, polyhouse_id }) => ({
                url: endpoint, // Pass dynamic endpoint here
                method: 'GET',
                params: { polyhouse_id },
            }),
            providesTags: ['Orgs'], // Associate "Orgs" cache with this query
        }),

        updateUser: builder.mutation({
            query: ({ updates, endpoint }) => ({
                url: `${endpoint}`,
                method: 'PUT',
                body: updates,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Orgs', id }, 'Orgs'],
        }),

        deleteUser: builder.mutation({
            query: (endpoint) => ({
                url: `${endpoint}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Orgs', id }, 'Orgs'], // Invalidate specific ID and list
        }),

        // Stocks 
        addStocks: builder.mutation({
            query: ({ values }) => ({
                url: `add/stocks`, // Updated URL to match other endpoint conventions
                method: 'POST',
                body: { ...values },
            }),
            invalidatesTags: ['Stocks'], // Invalidate all stocks cache after adding
        }),

        // Get all stocks
        getStocks: builder.query({
            query: ({ polyhouse_id }) => ({
                url: `stocks`,
                params: { polyhouse_id }, // Attach polyhouse_id as a query parameter
            }),
            providesTags: (result) =>
                result
                    ? result?.data?.map((stock) => ({ type: 'Stocks', id: stock.id }))
                    : ['Stocks'],
        }),

        // Update stock
        updateStock: builder.mutation({
            query: ({ updates, stockId }) => ({
                url: `edit/stocks/${stockId}`, // Corrected endpoint for stock update
                method: 'PUT',
                body: updates,
            }),
            invalidatesTags: (result, error, { stockId }) => [
                { type: 'Stocks', id: stockId },
                'Stocks', // Invalidate the specific stock and the list of stocks
            ],
        }),

        // Delete stock
        deleteStock: builder.mutation({
            query: ({ stockId }) => ({
                url: `delete/stocks/${stockId}`, // Corrected endpoint for stock delete
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, stockId) => [
                { type: 'Stocks', id: stockId },
                'Stocks', // Invalidate the specific stock and the list of stocks
            ],
        }),

        // Get Job Card Data
        getJobCardData: builder.query({
            query: ({ polyhouse_id }) => ({

                url: `jobs_allotment`,
                params: { polyhouse_id },
            }),
            providesTags: (result) =>
                result?.data
                    ? result.data.map((jobCard) => ({ type: 'Jobs', id: jobCard.id }))
                    : ['Jobs'],
        }),

        getJobCardDataById: builder.query({
            query: ({ id, polyhouse_id }) => ({
                url: `single_job/${id}`,
                params: { polyhouse_id },
            }),
            providesTags: (result) =>
                result?.id
                    ? [{ type: 'Jobs', id: result.id }]
                    : ['Jobs'], // Fallback if result.id is not available
        }),

        addJobCardData: builder.mutation({
            query: ({ values }) => ({
                url: `add/jobs_allotment`,
                method: 'POST',
                body: { ...values },
            }),
            invalidatesTags: ['Jobs'],
        }),

        editJobCardData: builder.mutation({
            query: ({ updates, jobId }) => ({
                url: `edit/jobs_allotment/${jobId}`,
                method: 'PUT',
                body: updates,
            }),
            invalidatesTags: (result, error, { jobId }) => [
                { type: 'Jobs', id: jobId },
                'Jobs',
            ],
        }),
        deleteJobCardData: builder.mutation({
            query: ({ jobId }) => ({
                url: `delete/jobs_allotment/${jobId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, jobId) => [
                { type: 'Jobs', id: jobId },
                'Jobs',
            ],
        }),

        //Labour
        addLabour: builder.mutation({
            query: ({ values }) => ({
                url: `add_labours_data`,
                method: 'POST',
                body: { ...values }
            })
        }),
        fetchLaboursData: builder.query({
            query: ({ polyhouse_id }) => ({
                url: `labours_data`,
                method: 'GET',
                params: { polyhouse_id },
            })
        }),
        updateLabourDataById: builder.mutation({
            query: ({updates, labour_id }) => ({
                url: `edit/labours_data/${labour_id}`,
                method: 'PUT',
                body: updates,
            }),
            invalidatesTags: (result, error, { labour_id }) => [
                { type: 'Labours', id: labour_id },
                'Labours',
            ],
        }),
        //Suppliers Data

        addSupplier: builder.mutation({
            query: ({ values }) => ({
                url: `add/suppliers`,
                method: 'POST',
                body: { ...values },
            }),
            invalidatesTags: ['Suppliers'], // Invalidate all "Orgs" cache on addition
        }),
        getSuppliersData: builder.query({
            query: ({ polyhouse_id }) => ({
                url: `suppliers`,
                params: { polyhouse_id },
            }),
            providesTags: (result) =>
                result?.data
                    ? result.data.map((supplier) => ({ type: 'Suppliers', id: supplier._id }))
                    : ['Suppliers'],
        }),
        updateSuppliersData: builder.mutation({
            query: ({ updates, supplierId }) => ({
                url: `edit/suppliers/${supplierId}`,
                method: 'PUT',
                body: updates,
            }),
            invalidatesTags: (result, error, { supplierId }) => [
                { type: 'Suppliers', id: supplierId },
                'Suppliers',
            ],
        }),
        deleteSuppliersData: builder.mutation({
            query: ({ supplierId }) => ({
                url: `delete/suppliers/${supplierId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, supplierId) => [
                { type: 'Suppliers', id: supplierId },
                'Suppliers',
            ],
        }),

        // Available Modules
        getAvailableModules: builder.query({
            query: () => ({
                url: `available_modules`,
            }),
            providesTags: (result) =>
                result?.data
                    ? result.data.map((module) => ({ type: 'Modules', id: module._id }))
                    : ['Modules'],
        }),

        // Selected Modules

        addSelectedModules: builder.mutation({
            query: ({ values, polyhouse_id }) => ({
                url: `add/selected_modules`,
                method: 'POST',
                body: { ...values },
                params: { polyhouse_id },
            }),
            invalidatesTags: ['Modules'],
        }),
        getSelectedModules: builder.query({
            query: ({ polyhouse_id }) => ({
                url: `selected_modules`,
                params: { polyhouse_id },
            }),
            providesTags: (result) =>
                result?.data
                    ? result.data.map((module) => ({ type: 'Modules', id: module._id }))
                    : ['Modules'],
        }),
        deleteSelectedModule: builder.mutation({
            query: ({ moduleId }) => ({
                url: `delete/selected_modules/${moduleId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, moduleId) => [
                { type: 'Modules', id: moduleId },
                'Modules',
            ],
        }),


        //Polyhouses
        getPolyHouses: builder.query({
            query: () => ({
                url: `polyhouses`,

            }),
            providesTags: (result) =>
                result?.data
                    ? result.data.map((polyhouse) => ({ type: 'Polyhouses', id: polyhouse._id }))
                    : ['Polyhouses'],
        }),
        getPolyHouseById: builder.query({
            query: ({ polyhouse_id }) => ({
                url: `single_polyhouse`,
                params: { polyhouse_id },
            }),
            // providesTags: (result) =>
            //     result?.data
            //         ? result.data.map((polyhouse) => ({ type: 'Polyhouses', id: polyhouse._id }))
            //         : ['Polyhouses'],
        }),
        addNewPolyHouse: builder.mutation({
            query: ({ values }) => ({
                url: `add/polyhouses`,
                method: 'POST',
                body: { ...values }
            })
        }),

        updatePolyHouseDetails: builder.mutation({
            query: ({ updates, polyId }) => ({
                url: `edit/polyhouses/${polyId}`,
                method: 'PUT',
                body: updates,
            }),
            invalidatesTags: (result, error, { polyId }) => [
                { type: 'Polyhouses', id: polyId },
                'Polyhouses',
            ],
        }),
        deletePolyHouse: builder.mutation({
            query: ({ polyId }) => ({
                url: `delete/polyhouses/${polyId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, polyId) => [
                { type: 'Polyhouses', id: polyId },
                'Polyhouses',
            ]
        }),

      
    }),
});

export const {
    // User
    useAddUserMutation,
    useFetchUsersQuery,
    useUpdateUserMutation,
    useDeleteUserMutation,
   
  
    


} = apiSlice;
